/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  HStack,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react'
import { ScaleFade } from '@chakra-ui/transition'
import Link, { LinkProps } from 'next/link'
import { useRouter } from 'next/router'

import {
  Logo,
  Sticky,
  Button,
  Text,
  TextProps,
  StickyProps,
} from '@gaia-family/ds'
import { breakpoints } from '@gaia-family/ds/theme/tokens'
import { env } from '@gaia-family/lib'

import { routes } from '../middleware/auth'

import { TreatmentProfileStatus, useNavigationQuery } from '@graphql'

import { Routes } from '@hooks/useRouting'

export const NAV_HEIGHT = 88

const DropdownLink: FC<LinkProps & TextProps> = ({
  children,
  href,
  ...props
}) => (
  <Link href={href} passHref>
    <Text
      as="a"
      px={1}
      textAlign="right"
      textStyle="cta"
      size="sm"
      _hover={{
        color: 'orange',
      }}
      {...props}
    >
      {children}
    </Text>
  </Link>
)

const NavLink: FC<LinkProps & TextProps> = ({ children, href }) => (
  <Link href={href} passHref>
    <Text
      as="a"
      textAlign="right"
      textStyle="cta"
      size="sm"
      _hover={{
        color: 'orange',
      }}
    >
      {children}
    </Text>
  </Link>
)

const UserNavPopover = ({
  user,
  onSignOut,
  isOpen,
  onClose,
}: {
  user: User
  isOpen: boolean
  onSignOut: () => void
  onClose: () => void
}) => {
  const router = useRouter()
  const firstName = user?.firstName
  const { pathname } = router
  useEffect(() => {
    router.events.on('routeChangeComplete', onClose)
    return () => {
      router.events.off('routeChangeComplete', onClose)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <HStack spacing={12}>
      <PopoverTrigger>
        <Button
          variant={isOpen ? 'secondary' : 'tertiary'}
          transition="all 0.2s ease-out"
          width="auto"
          data-cy="user-menu"
        >
          <Text variant="B3" fontWeight="normal">
            <FormattedMessage
              defaultMessage="{firstName}"
              values={{ firstName }}
            />
          </Text>
        </Button>
      </PopoverTrigger>

      <PopoverContent layerStyle="softBlack" p="lg" minWidth="summary">
        <VStack spacing="md" alignItems="stretch" width="100%">
          {pathname !== routes.Routes.profile && (
            <DropdownLink href={routes.Routes.profile}>
              <FormattedMessage defaultMessage="Profile" />
            </DropdownLink>
          )}
          <DropdownLink
            href={routes.Routes.profileTreatmentUpdate}
            data-cy="fertility-profile"
          >
            <FormattedMessage defaultMessage="Fertility profile" />
          </DropdownLink>
          <DropdownLink href={Routes.account}>
            <FormattedMessage defaultMessage="Account information" />
          </DropdownLink>
          <DropdownLink
            href="#"
            color="white"
            onClick={(e) => {
              e.preventDefault()
              onSignOut()
              onClose()
            }}
            data-cy="logout"
          >
            <FormattedMessage defaultMessage="Log out" />
          </DropdownLink>
        </VStack>
      </PopoverContent>
    </HStack>
  )
}

const UnauthedProfileNav = () => (
  <HStack spacing="md">
    <Link href={Routes.signIn} passHref>
      <Button
        transition="all 0.2s ease-out"
        as="a"
        variant="secondary"
        width="auto"
        display={['none', 'inline-block']}
      >
        <FormattedMessage defaultMessage="Sign in" />
      </Button>
    </Link>
  </HStack>
)

type User = { firstName?: string; lastName?: string; id: string }
export type NavigationBarProps = {
  blend?: StickyProps['background']
  user?: null | User
  onSignIn?: () => void
  onSignOut?: () => void
  hideMenu?: boolean
}

const routesWithoutViewProfile: string[] = [routes.Routes.profile]

export const NavigationBar: FC<NavigationBarProps> = ({
  user,
  blend,
  onSignOut,
  hideMenu,
}) => {
  const [hasScrolled, setHasScrolled] = useState(false)

  useEffect(() => {
    function updatePosition() {
      setHasScrolled(window.pageYOffset > 0)
    }
    window.addEventListener('scroll', updatePosition)
    updatePosition()
    return () => window.removeEventListener('scroll', updatePosition)
  }, [])
  const router = useRouter()
  const { pathname } = router
  const { data } = useNavigationQuery({ skip: !user })

  const pathwayComplete =
    data?.viewer?.user?.treatmentProfile?.status ===
    TreatmentProfileStatus.Complete

  const protocol = env.NODE_ENV === 'production' ? 'https' : 'http'
  const canonicalUrl = `${protocol}://${env.NEXT_PUBLIC_URL}/`
  const logoHref = user ? routes.Routes.profile : canonicalUrl

  const [isLargerThanMobile] = useMediaQuery(`(min-width:${breakpoints.sm})`)

  return (
    <Sticky
      flexDirection="column"
      position={{
        top: 0,
        insetX: 0,
        position: 'sticky',
      }}
      background={!hasScrolled && blend ? blend : 'softerWhite'}
      width="100%"
      zIndex="sticky"
      transition="background 0.2s ease-out"
      h={NAV_HEIGHT}
    >
      <Flex
        direction="column"
        width="100%"
        flex="1"
        borderBottomWidth={1}
        transition="border-bottom-color 0.2s ease-out"
        borderBottomColor={hasScrolled ? 'gray' : 'transparent'}
      >
        <Flex
          flex="1"
          width="100%"
          marginX="auto"
          maxWidth="container.xl"
          py="md"
          transition="padding 0.3s ease-out"
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack spacing={['sm', 'md']} direction="row" alignItems="center">
            <Link passHref href={logoHref}>
              <a>
                <Logo
                  transition="width 0.5s ease-out"
                  color="softBlack"
                  width="font.xxxxl"
                  display={['block', null, null, 'block']}
                />
              </a>
            </Link>
          </HStack>
          {!hideMenu ? (
            <HStack alignItems="center" spacing="lg">
              {user &&
              pathwayComplete &&
              !routesWithoutViewProfile.includes(pathname) &&
              isLargerThanMobile ? (
                <NavLink href={routes.Routes.profile}>
                  <FormattedMessage defaultMessage="View profile" />
                </NavLink>
              ) : null}
              {user && onSignOut ? (
                <ScaleFade in={true} initialScale={0.9}>
                  <Popover placement="bottom-end">
                    {({ isOpen, onClose }) => (
                      <UserNavPopover
                        onSignOut={onSignOut}
                        user={user}
                        isOpen={isOpen}
                        onClose={onClose}
                      />
                    )}
                  </Popover>
                </ScaleFade>
              ) : null}

              {!user ? (
                <ScaleFade in={true} initialScale={0.9}>
                  <UnauthedProfileNav />
                </ScaleFade>
              ) : null}
            </HStack>
          ) : null}
        </Flex>
      </Flex>
    </Sticky>
  )
}
